import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCServicesApi, RCCTooltip } from "react-common-components";
import SidebarCard from "src/components/SidebarCard";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import Title from "src/components/Title";
import { IRefSidebarCard } from "src/components/SidebarCard/interface";
import AppIcons from "src/components/AppIcons";
import { useParams } from "react-router-dom";
import FiltersContainer from "src/components/FiltersContainer";
import { useForm } from "react-hook-form";
import LovCanvasModels from "src/components/Lov/CanvasModels";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import SidebarCanvas from "./components/SidebarCanvas";
import { ICatbb001, IFiltersCanvas } from "./interface";
import Modals from "./components/SidebarCanvas/modals";
import { IRefICanvasModals } from "./components/SidebarCanvas/interface";
import ModelCanvas from "./components/ModelCanvas";

const Canvas: React.FC = () => { 

	const params = useParams<{
		canvasId?: string;
	}>();


	const { addToast } = RCCHookToast();
	const {translate} = useTranslation();

	const sidebarCardRef = useRef<IRefSidebarCard>(null);

	const refModals = useRef<IRefICanvasModals>(null);

	const [titleCanvas, setTitleCanvas] = useState<string>("");

	const [selectedCanvas, setSelectedCanvas] = useState<ICatbb001 | undefined>(undefined);
	const [cardsCanvas, setCardsCanvas] = useState<ICatbb001[]>([]);
	const [showFilters, setShowFilters] = useState(false);

	const [filters, setfilters] = useState<IFiltersCanvas>({ 
		filter: "",
		type: ""
	});

	const { 
		handleSubmit : handleSubmitFilters, 
		control: controlFilters, 
		register: registerFilters,
		reset: resetFilters
	} = useForm();

	const requestCanvas = async (selectNewCanvas?: boolean, selectedCanvasId?: number) => {
		const hasRegister = cardsCanvas.map(({ cab001Id }) => cab001Id);

		try {
			const response = await RCCServicesApi.getRequest({
				method: "Canvas/List",
				urlParameters: {
					filter: filters.filter ?? "",
					type: filters.type ?? ""
				}
			});

			const data = response.data as ICatbb001[];
			setCardsCanvas(data);

			if(selectedCanvasId){
				const canvasObj : ICatbb001 | undefined = data.find((x: ICatbb001) => x.cab001Id === selectedCanvasId);
				if(canvasObj)
					onClickCardFuncion(canvasObj);	
			}
			
			if(selectNewCanvas){
				const newRegister = data.find((obj) => !hasRegister.includes(obj.cab001Id));
				setSelectedCanvas(newRegister);
			}

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao buscar os dados"), addToast);
		}
	};

	const callbackDeleteCanvas = (id: number) => {
		setCardsCanvas((prevState) => prevState.filter((item) => item.cab001Id !== id));
		if(selectedCanvas){
			setSelectedCanvas(undefined);
		}
	};

	const callbackAddCanvas = async () => {
		await requestCanvas(true);
	};

	const callbackChangeCanvas = async () => {
		await requestCanvas();
	};

	const onClickCardFuncion = (canvas: ICatbb001) => {
		if(!canvas || selectedCanvas?.cab001Id === canvas.cab001Id){
			setSelectedCanvas(undefined);
			return;
		}
		setSelectedCanvas(canvas);
		sidebarCardRef.current?.changeListVisibility();
	};

	useEffect(() => {
		const initialCanvas = params?.canvasId ? parseInt(params?.canvasId, 10) : undefined;
		requestCanvas(false, initialCanvas);
	}, []);

	useEffectSkipInitialRender(() => {
		requestCanvas();
	}, [filters]);

	useEffect(() => {
		if(!selectedCanvas){
			setTitleCanvas("");
			return;
		}
		const title = cardsCanvas.find(({ cab001Id }) => cab001Id === selectedCanvas.cab001Id)?.cab001Descricao;
		if(title)
			setTitleCanvas(title);
	}, [selectedCanvas, cardsCanvas]);

	const submitFilters = (data: any) => {
		setSelectedCanvas(undefined);
		setfilters({
			filter: data.filter,
			type: typeof data.type === "object" ? data?.type?.value : data?.type
		});
	};

	return(
		<>
			<SidebarCard 
				ref={sidebarCardRef}
				title={translate("Canvas")}
				icon={() => <AppIcons name="businessDiagnosis" />}
				buttonsContent={
					<>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							<RCCTooltip
								text={translate("Adicionar Canvas")}
								color="purple"
							>
								<RCCButton
									icon={{name: "plus", color: "#FFF", size: 16}}
									color="purple"
									layout="isRounded"
									size="md"
									onClick={() => refModals.current?.actionButtonAdd()}
								/>
							</RCCTooltip>									
						</CheckPermission>
						<RCCTooltip
							text={translate("Filtros")}
							color={showFilters || filters.filter || filters.type ? "blue" : "grey"}
						>
							<RCCButton
								icon={{name: "filter", color: "#FFF", size: 16}}
								color={showFilters || filters.filter || filters.type  ? "blue" : "grey"}
								layout="isRounded"
								size="md"
								onClick={() => setShowFilters((prevState) => !prevState)}
							/>
						</RCCTooltip>
					</>
				}
				customCards={<>
					{!cardsCanvas || cardsCanvas.length === 0 ? (
						<RCCAlert
							description={translate("Não foram encontrados dados")}
						/>
					): (
						<SidebarCanvas 
							items={cardsCanvas}
							setItems={setCardsCanvas}
							refModalCanvas={refModals}
							onClickCardFuncion={onClickCardFuncion}
							selectedCanvas={selectedCanvas?.cab001Id}
						/>
					)}
				</>}
				filterContent={
					<>
						<FiltersContainer
							onSubmitFilters={handleSubmitFilters(submitFilters)}
							resetFilters={() => resetFilters({ 
								filter: "",
								type: ""
							})}
							showFilters={showFilters}
						>
							<RCCGrid
								container 
								item
								xs={12}
							>
								<LovCanvasModels 
									control={controlFilters}
									name="type"
									label={translate("Modelo de canvas")}
									defaultValue={filters.type || ""}
									submit="value and label"
									isClearable
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
							>
								<RCCInput 
									register={registerFilters}
									name="filter"
									label={translate("Pesquisar")}
									defaultValue={filters.filter}
								/>
							</RCCGrid>
						</FiltersContainer>
					</>
				}
				content={<>
					{selectedCanvas ? (
						<>
							<Title color="purple" size="xl">
								{titleCanvas}
							</Title>
							<ModelCanvas 
								canvasModelId={selectedCanvas?.cab001Caa001Id} 
								canvasSelectedId={selectedCanvas?.cab001Id} 
								canvasSelectedCode={selectedCanvas?.caa001Codigo}
							/>
						</>
					) : (
						<RCCAlert
							color="purple"
							title={translate("Nenhum Canvas selecionado")}
							description={translate("Selecione ou adicione um Canvas para visualizar os dados")}
						/>
					)}
				</>
				}
			/>
			<Modals 
				ref={refModals} 
				callbackAddCanvas={callbackAddCanvas}
				callbackChangeCanvas={callbackChangeCanvas}
				callbackDeleteCanvas={callbackDeleteCanvas}
			/>		
		</>
	);
};

export default Canvas;
